import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, BarChart3, BookOpen, Briefcase, ClipboardCheck, CirclePlay, Clock, DoorOpen, FileText, FlaskConical, Link2,
  MessagesSquare, Package, Pill, School, Share2, Target, TriangleAlert, UserX, Warehouse,
} from "lucide-react";
import Footer from "../components/Footer";
import "./LandingPage.css";

// What's in the hero's scene: a drawn room from the Game Builder's own picture library, with the things a player can act
// on marked the way the builder marks them. x/y are percentages of the 3:2 picture; `flip` puts the label on the left; `wide` ones only show on wider screens.
const HOTSPOTS = [
  { icon: DoorOpen, kind: "Door", name: "To the loading bay", x: 53, y: 36, flip: true },
  { icon: Package, kind: "Object", name: "Steel shelving", x: 9, y: 48, wide: true },
  { icon: TriangleAlert, kind: "Hazard", name: "Open inspection pit", x: 47, y: 84 },
];

// The template simulations every demo starts with (backend game_builder/demo_templates/) - each made from a manual like these.
const INDUSTRIES = [
  { img: "warehouse", icon: Warehouse, title: "Forklift & aisle safety SOP", text: "Park an abandoned forklift, keys out; empty a damaged rack bay." },
  { img: "pharmacy", icon: Pill, title: "Dispensing procedures", text: "Make up a suspension for a customer; put a cold-chain delivery away." },
  { img: "lab", icon: FlaskConical, title: "Lab close-down checklist", text: "Clear the emergency equipment; shut down a distillation safely." },
  { img: "office", icon: Briefcase, title: "Clean-desk policy", text: "Lock the screen and file the confidential papers before you leave." },
];

const STEPS = [
  { icon: FileText, title: "Upload your manual", text: "The Word or PDF file of the SOP, manual or policy you already use - or describe the job in a short chat." },
  { icon: MessagesSquare, title: "Answer a few questions", text: "Only what the manual doesn't say: who plays, and the mistakes people really make." },
  { icon: ClipboardCheck, title: "Review - don't build", text: "SimulateJob drafts each situation and draws the room. You check it and approve." },
  { icon: Share2, title: "Share a link", text: "Your people play in the browser - no account, no install." },
];

const FEATURES = [
  { icon: UserX, title: "No technical team", text: "No developers, instructional designers, agencies or IT tickets - the people who know the job build it." },
  { icon: BarChart3, title: "See how people did", text: "Plays, finishes, the usual time and the mistakes made most often." },
  { icon: School, title: "Fits your learning system", text: "Download any simulation as a SCORM 1.2 package for your LMS." },
  { icon: Link2, title: "Nothing to install", text: "Learners open a link on a laptop or phone and play straight away." },
];

const PROMISES = [
  { icon: BookOpen, text: "Uses the manuals you already have" },
  { icon: Clock, text: "Your first simulation in minutes" },
  { icon: UserX, text: "No technical team needed" },
];

// The public front page (logistics-workflow serves a static landing.html; a React page keeps this app in one build).
export default function LandingPage() {
  return (
    <div className="lp">
      <header className="lp-nav">
        <Link to="/" className="lp-brand"><img src="/logo.svg" alt="" /> SimulateJob</Link>
        <nav className="lp-links" aria-label="Sections">
          <a href="#how">How it works</a>
          <a href="#industries">Examples</a>
          <Link to="/book-demo">Book a demo</Link>
        </nav>
        <div className="lp-actions">
          <Link to="/login" className="lp-signin">Sign in</Link>
          <Link to="/register" className="lp-btn lp-btn-primary">Get started <ArrowRight size={18} /></Link>
        </div>
      </header>

      <section className="lp-hero">
        <div className="lp-hero-text">
          <p className="lp-eyebrow">For safety, training and operations teams</p>
          <h1>Your manuals, turned into simulations <span>in minutes.</span></h1>
          <p className="lp-lead">
            Upload the SOPs and manuals you already have. SimulateJob turns them into hands-on simulations your people play in
            the browser - without developers, agencies or IT tickets.
          </p>
          <div className="lp-cta">
            <Link to="/register" className="lp-btn lp-btn-primary">Start with your manual <ArrowRight size={18} /></Link>
            <Link to="/try-demo" className="lp-btn lp-btn-ghost"><CirclePlay size={20} /> Try the demo</Link>
          </div>
          <ul className="lp-promises">
            {PROMISES.map(({ icon: Icon, text }) => <li key={text}><Icon size={22} aria-hidden="true" /> {text}</li>)}
          </ul>
        </div>

        <div className="lp-scene" aria-label="A warehouse scene from a training simulation, with the things the player can act on marked">
          <div className="lp-stage">
            <img src="/assets/landing/hero-warehouse.jpg" alt="" />
            {HOTSPOTS.map(({ icon: Icon, kind, name, x, y, flip, wide }) => (
              <div key={name} className={`lp-hotspot${flip ? " is-flipped" : ""}${wide ? " is-wide-only" : ""}`} style={{ left: `${x}%`, top: `${y}%` }}>
                <span className="lp-dot" aria-hidden="true" />
                <div className="lp-tag"><Icon size={18} aria-hidden="true" /><div><strong>{kind}</strong><span>{name}</span></div></div>
              </div>
            ))}
          </div>
          <div className="lp-goal">
            <div className="lp-goal-head"><Target size={20} aria-hidden="true" /> <strong>Mission goal</strong></div>
            <p>Close the bay at the end of the shift: cover the pit, clear the shelving and lock the loading-bay door.</p>
            <div className="lp-goal-count">0 / 3 done</div>
          </div>
        </div>
      </section>

      <section className="lp-band" id="industries">
        <div className="lp-wrap">
          <div className="lp-band-head">
            <div>
              <p className="lp-eyebrow">Made from manuals like yours</p>
              <h2>Every workplace has a manual. Each one can be practised.</h2>
            </div>
            <Link to="/try-demo" className="lp-more">Play these in the demo <ArrowRight size={18} /></Link>
          </div>
          <div className="lp-cards">
            {INDUSTRIES.map(({ img, icon: Icon, title, text }) => (
              <article key={title} className="lp-card">
                <div className="lp-card-media">
                  <div className="lp-card-img"><img src={`/assets/landing/card-${img}.jpg`} alt="" loading="lazy" /></div>
                  <span className="lp-card-badge"><Icon size={18} aria-hidden="true" /></span>
                </div>
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="lp-wrap lp-how" id="how">
        <p className="lp-eyebrow">How it works</p>
        <h2>From manual to simulation - no technical team.</h2>
        <ol className="lp-steps">
          {STEPS.map(({ icon: Icon, title, text }, n) => (
            <li key={title}>
              <span className="lp-step-n">{n + 1}</span>
              <Icon size={24} aria-hidden="true" />
              <h3>{title}</h3>
              <p>{text}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="lp-features">
        <div className="lp-wrap lp-features-row">
          {FEATURES.map(({ icon: Icon, title, text }) => (
            <div key={title} className="lp-feature">
              <Icon size={30} aria-hidden="true" />
              <div><h3>{title}</h3><p>{text}</p></div>
            </div>
          ))}
        </div>
      </section>

      <section className="lp-wrap lp-closing">
        <h2>Bring a manual. Leave with a simulation.</h2>
        <p>On a short call we&rsquo;ll turn one of your own manuals into a simulation while you watch.</p>
        <div className="lp-cta">
          <Link to="/book-demo" className="lp-btn lp-btn-primary">Book a demo <ArrowRight size={18} /></Link>
          <Link to="/register" className="lp-btn lp-btn-ghost">Create your team&rsquo;s account</Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
